import { Add, Close } from '@mui/icons-material';
import { FormHelperText, IconButton, InputAdornment } from '@mui/material';
import React from 'react';
import { Button } from 'new/elements/Button';
import { Select } from 'new/elements/Select';
import { TextField } from 'new/elements/TextField';
import styles from './KeyValueEditor.module.scss';

export type KVRowType = 'string' | 'number';

export const KV_ROW_TYPES: { value: KVRowType; label: string }[] = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
];

export interface KVRow {
  id: string;
  key: string;
  value: string;
  type: KVRowType;
}

interface KeyValueEditorProps {
  rows: KVRow[];
  onChange: (rows: KVRow[]) => void;
  duplicateRowIds?: Set<string>;
  missingKeyRowIds?: Set<string>;
  invalidNumberRowIds?: Set<string>;
  addLabel?: string;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
}

export function KeyValueEditor({
  rows,
  onChange,
  duplicateRowIds = new Set(),
  missingKeyRowIds = new Set(),
  invalidNumberRowIds = new Set(),
  addLabel = 'Add',
  keyPlaceholder = 'Key',
  valuePlaceholder = 'Value',
}: KeyValueEditorProps) {
  const handleKeyChange = (id: string, key: string) => {
    onChange(rows.map((r) => (r.id === id ? { ...r, key } : r)));
  };

  const handleValueChange = (id: string, value: string) => {
    onChange(rows.map((r) => (r.id === id ? { ...r, value } : r)));
  };

  const handleTypeChange = (id: string, type: KVRowType) => {
    onChange(rows.map((r) => (r.id === id ? { ...r, type } : r)));
  };

  const handleRemove = (id: string) => {
    onChange(rows.filter((r) => r.id !== id));
  };

  const handleAdd = () => {
    const id = `kv-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
    onChange([...rows, { id, key: '', value: '', type: 'string' }]);
  };

  return (
    <div className={styles.container}>
      {rows.map((row) => {
        const isDupe = duplicateRowIds.has(row.id);
        const isMissingKey = missingKeyRowIds.has(row.id);
        const isInvalidNumber = invalidNumberRowIds.has(row.id);
        return (
          <div key={row.id} className={styles.row}>
            <div className={styles.field}>
              <TextField
                fullWidth
                placeholder={keyPlaceholder}
                value={row.key}
                error={isDupe || isMissingKey}
                onChange={(e) => handleKeyChange(row.id, e.target.value)}
              />
              <div className={styles.errorSlot}>
                {isDupe && (
                  <FormHelperText error className={styles.error}>
                    Duplicate key
                  </FormHelperText>
                )}
                {isMissingKey && (
                  <FormHelperText error className={styles.error}>
                    Key is required
                  </FormHelperText>
                )}
              </div>
            </div>
            <div className={styles.field}>
              <TextField
                fullWidth
                placeholder={valuePlaceholder}
                value={row.value}
                error={isInvalidNumber}
                onChange={(e) => handleValueChange(row.id, e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" className={styles.valueAdornment}>
                      <div className={styles.typeSelect}>
                        <Select
                          items={KV_ROW_TYPES.map(({ value, label }) => ({ value, title: label }))}
                          value={row.type ?? 'string'}
                          onChange={(e) => handleTypeChange(row.id, e.target.value as KVRowType)}
                          disableUnderline
                        />
                      </div>
                    </InputAdornment>
                  ),
                }}
              />
              <div className={styles.errorSlot}>
                {isInvalidNumber && (
                  <FormHelperText error className={styles.error}>
                    Must be a valid number
                  </FormHelperText>
                )}
              </div>
            </div>
            <IconButton
              className={styles.removeButton}
              size="small"
              aria-label="Remove row"
              onClick={() => handleRemove(row.id)}
            >
              <Close />
            </IconButton>
          </div>
        );
      })}

      <Button
        variant="outlined"
        className={styles.addButton}
        startIcon={<Add />}
        onClick={handleAdd}
      >
        {addLabel}
      </Button>
    </div>
  );
}
